import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiArrowRight,
  FiCheck,
} from 'react-icons/fi';
import api from '../axios';
import { useAuth } from '../context/AuthContext';

const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 49,
    description: 'Full platform access with monthly billing.',
    billing: 'month',
    audience: 'Flexible billing',
    features: [
      { name: 'Configurable symbols and limits', value: 'Forex, crypto, max trades, and pending orders', included: true },
      { name: 'Daily risk controls', value: 'Profit and loss thresholds', included: true },
      { name: 'Weekday and weekend settings', value: 'Separate trade caps', included: true },
      { name: 'Full account access', value: 'MT5 connection and automation controls', included: true },
    ],
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: 490,
    description: 'Full platform access with yearly billing.',
    billing: 'year',
    audience: 'Best value',
    features: [
      { name: 'Configurable symbols and limits', value: 'Forex, crypto, max trades, and pending orders', included: true },
      { name: 'Daily risk controls', value: 'Profit and loss thresholds', included: true },
      { name: 'Weekday and weekend settings', value: 'Separate trade caps', included: true },
      { name: 'Full account access', value: 'MT5 connection and automation controls', included: true },
      { name: 'Effective monthly cost', value: '$40.83 per month when billed yearly', included: true },
    ],
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const { isAuthed, isLoading } = useAuth();
  const activePlan = plans.find((plan) => plan.id === billingCycle) || plans[0];

  const handleSelectPlan = async (planId) => {
    if (isLoading) {
      return;
    }

    if (!isAuthed) {
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const response = await api.post('/api/subscription/create-intent', {
        plan_id: planId,
      });

      if (response.data?.client_secret) {
        navigate('/checkout', {
          state: {
            clientSecret: response.data.client_secret,
            paymentIntentId: response.data.payment_intent_id,
            planId,
            amount: response.data.amount,
            planName: plans.find((plan) => plan.id === planId)?.name,
          },
        });
      } else {
        toast.error('Failed to create payment intent');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process plan selection');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <section className="px-4 pb-20 pt-32 sm:px-6 lg:px-8 lg:pt-36">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
            <div>
              <h1 className="max-w-4xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Choose
                <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  monthly or yearly
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                One plan. Two billing options.
              </p>
              <div className="mt-10 grid gap-3 sm:grid-cols-2">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`rounded-2xl px-5 py-4 text-left transition ${billingCycle === 'monthly' ? 'bg-white text-slate-950' : 'border border-white/12 bg-white/5 text-white hover:bg-white/10'}`}
                >
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] opacity-70">Monthly</div>
                  <div className="mt-2 text-3xl font-black">$49</div>
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`rounded-2xl px-5 py-4 text-left transition ${billingCycle === 'yearly' ? 'bg-white text-slate-950' : 'border border-white/12 bg-white/5 text-white hover:bg-white/10'}`}
                >
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] opacity-70">Yearly</div>
                  <div className="mt-2 text-3xl font-black">$490</div>
                </button>
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7 backdrop-blur-xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-[0.22em] text-cyan-200/70">{activePlan.audience}</p>
                  <h2 className="mt-3 text-3xl font-black text-white">{activePlan.name}</h2>
                  <p className="mt-3 text-sm leading-7 text-slate-400">{activePlan.description}</p>
                </div>
              </div>

              <div className="mt-8 rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-5">
                <div className="flex items-end gap-2">
                  <span className="text-5xl font-black text-white">${activePlan.price}</span>
                  <span className="pb-1 text-sm text-slate-400">/{activePlan.billing}</span>
                </div>
              </div>

              <button
                onClick={() => handleSelectPlan(activePlan.id)}
                disabled={loading && selectedPlan === activePlan.id}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-4 font-bold text-slate-950 transition duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading && selectedPlan === activePlan.id ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Processing...
                  </>
                ) : (
                  <>
                    Continue
                    <FiArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <div className="mt-8">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-500">Included</p>
                <div className="mt-5 space-y-4">
                  {activePlan.features.map((feature) => (
                    <div key={`${activePlan.id}-${feature.name}`} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-slate-950/45 p-3">
                      <FiCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                      <div>
                        <p className="text-sm font-medium text-slate-200">{feature.name}</p>
                        {feature.value && <p className="mt-1 text-xs text-slate-400">{feature.value}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
